import React, { useState, useEffect, useMemo } from 'react';
import { FaFilter, FaTimes, FaCheckCircle, FaClock, FaTimesCircle, FaSlidersH } from 'react-icons/fa';
import './MapFilters.css';
import { getTranslation } from '../../utils/translations';

// Format price in a short, human-friendly way (e.g. 500K, 1.2M)
const formatShortPrice = (value, language) => {
  if (value >= 1_000_000) {
    const m = value / 1_000_000;
    const rounded = m % 1 === 0 ? m.toFixed(0) : m.toFixed(1);
    return language === 'ar' ? `${rounded} مليون` : `${rounded}M`;
  }
  if (value >= 1000) {
    const k = value / 1000;
    const rounded = k % 1 === 0 ? k.toFixed(0) : k.toFixed(1);
    return language === 'ar' ? `${rounded} ألف` : `${rounded}K`;
  }
  return new Intl.NumberFormat(language === 'ar' ? 'ar-SA' : 'en-US').format(value);
};

// Build dynamic chips based on the actual min/max in the data
const buildPriceChips = (min, max, language) => {
  if (!isFinite(min) || !isFinite(max) || max <= min) return [];

  const span = max - min;
  // Pick a "nice" step that divides the span into ~4 buckets
  const rawStep = span / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const niceSteps = [1, 2, 2.5, 5, 10];
  const step =
    niceSteps.find(s => s * magnitude >= rawStep) * magnitude || magnitude;

  const breakpoints = [];
  let current = Math.ceil(min / step) * step;
  while (current < max && breakpoints.length < 4) {
    if (current > min) breakpoints.push(current);
    current += step;
  }

  const chips = [];
  if (breakpoints.length === 0) {
    return [];
  }

  // "Under X" — first chip
  chips.push({
    id: `under-${breakpoints[0]}`,
    label:
      language === 'ar'
        ? `أقل من ${formatShortPrice(breakpoints[0], language)}`
        : `Under ${formatShortPrice(breakpoints[0], language)}`,
    range: { min, max: breakpoints[0] }
  });

  // Middle chips: "X – Y"
  for (let i = 0; i < breakpoints.length - 1; i++) {
    chips.push({
      id: `range-${breakpoints[i]}-${breakpoints[i + 1]}`,
      label: `${formatShortPrice(breakpoints[i], language)} – ${formatShortPrice(breakpoints[i + 1], language)}`,
      range: { min: breakpoints[i], max: breakpoints[i + 1] }
    });
  }

  // "Over X" — last chip
  chips.push({
    id: `over-${breakpoints[breakpoints.length - 1]}`,
    label:
      language === 'ar'
        ? `أكثر من ${formatShortPrice(breakpoints[breakpoints.length - 1], language)}`
        : `Over ${formatShortPrice(breakpoints[breakpoints.length - 1], language)}`,
    range: { min: breakpoints[breakpoints.length - 1], max }
  });

  return chips;
};

const PriceChipsFilter = ({ language, priceStats, priceRange, onChange }) => {
  const chips = useMemo(
    () => buildPriceChips(priceStats.min, priceStats.max, language),
    [priceStats.min, priceStats.max, language]
  );

  const [showCustom, setShowCustom] = useState(false);
  const [customMin, setCustomMin] = useState('');
  const [customMax, setCustomMax] = useState('');

  // Determine which chip (if any) matches current range
  const activeChipId = useMemo(() => {
    const match = chips.find(
      c => c.range.min === priceRange.min && c.range.max === priceRange.max
    );
    return match?.id || null;
  }, [chips, priceRange.min, priceRange.max]);

  const isAllSelected =
    priceRange.min === priceStats.min && priceRange.max === priceStats.max;

  const handleChipClick = (chip) => {
    setShowCustom(false);
    if (activeChipId === chip.id) {
      // Toggle off — back to full range
      onChange({ min: priceStats.min, max: priceStats.max });
    } else {
      onChange(chip.range);
    }
  };

  const handleResetAll = () => {
    setShowCustom(false);
    setCustomMin('');
    setCustomMax('');
    onChange({ min: priceStats.min, max: priceStats.max });
  };

  const handleApplyCustom = () => {
    const minNum = parseFloat(customMin);
    const maxNum = parseFloat(customMax);
    const finalMin = isFinite(minNum) ? Math.max(minNum, priceStats.min) : priceStats.min;
    const finalMax = isFinite(maxNum) ? Math.min(maxNum, priceStats.max) : priceStats.max;
    if (finalMin > finalMax) return;
    onChange({ min: finalMin, max: finalMax });
  };

  return (
    <div className="price-chips-filter">
      <div className="price-chips-row">
        <button
          type="button"
          className={`price-chip ${isAllSelected && !showCustom ? 'active' : ''}`}
          onClick={handleResetAll}
        >
          {language === 'ar' ? 'الكل' : 'All'}
        </button>
        {chips.map(chip => (
          <button
            key={chip.id}
            type="button"
            className={`price-chip ${activeChipId === chip.id ? 'active' : ''}`}
            onClick={() => handleChipClick(chip)}
          >
            {chip.label}
          </button>
        ))}
        <button
          type="button"
          className={`price-chip price-chip-custom ${showCustom ? 'active' : ''}`}
          onClick={() => setShowCustom(v => !v)}
        >
          <FaSlidersH className="chip-icon" />
          {language === 'ar' ? 'مخصص' : 'Custom'}
        </button>
      </div>

      {showCustom && (
        <div className="price-custom-inputs">
          <div className="price-input-group">
            <label>{language === 'ar' ? 'من' : 'From'}</label>
            <input
              type="number"
              inputMode="numeric"
              placeholder={formatShortPrice(priceStats.min, language)}
              value={customMin}
              onChange={(e) => setCustomMin(e.target.value)}
              min={priceStats.min}
              max={priceStats.max}
            />
          </div>
          <div className="price-input-group">
            <label>{language === 'ar' ? 'إلى' : 'To'}</label>
            <input
              type="number"
              inputMode="numeric"
              placeholder={formatShortPrice(priceStats.max, language)}
              value={customMax}
              onChange={(e) => setCustomMax(e.target.value)}
              min={priceStats.min}
              max={priceStats.max}
            />
          </div>
          <button
            type="button"
            className="price-apply-btn"
            onClick={handleApplyCustom}
            disabled={!customMin && !customMax}
          >
            {language === 'ar' ? 'تطبيق' : 'Apply'}
          </button>
        </div>
      )}

      {!isAllSelected && (
        <div className="price-current-range">
          <span>
            {formatShortPrice(priceRange.min, language)} – {formatShortPrice(priceRange.max, language)}
            {' '}
            {language === 'ar' ? 'ر.س' : 'SAR'}
          </span>
        </div>
      )}
    </div>
  );
};

const MapFilters = ({ 
  isVisible, 
  onClose, 
  onFiltersChange, 
  language = 'ar',
  parcelsData = []
}) => {
  const [filters, setFilters] = useState({
    salesStatus: [],
    priceRange: { min: 0, max: 1000000 },
    services: []
  });

  const [priceStats, setPriceStats] = useState({ min: 0, max: 1000000 });
  const [isPriceFilterActive, setIsPriceFilterActive] = useState(false);
  const [statusCounts, setStatusCounts] = useState({
    Available: 0,
    'Not Available': 0,
    Reserved: 0,
    Sold: 0
  });
  const [availableServices, setAvailableServices] = useState([]);

  // Calculate statistics from parcels data
  useEffect(() => {
    if (parcelsData && parcelsData.length > 0) {
      // Calculate price range
      const prices = parcelsData
        .map(parcel => parseFloat(parcel.properties?.price_with_tax) || 0)
        .filter(price => price > 0);
      
      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        setPriceStats({ min: minPrice, max: maxPrice });
        setIsPriceFilterActive(false); // Reset price filter state when data changes
        setFilters(prev => ({
          ...prev,
          priceRange: { min: minPrice, max: maxPrice },
          isPriceFilterActive: false
        }));
      }

      // Calculate status counts
      const counts = { Available: 0, 'Not Available': 0, Reserved: 0, Sold: 0 };
      parcelsData.forEach(parcel => {
        const status = parcel.properties?.sales_status;
        if (counts.hasOwnProperty(status)) {
          counts[status]++;
        }
      });
      setStatusCounts(counts);

      // Extract available services
      const services = new Set();
      parcelsData.forEach(parcel => {
        const parcelServices = parcel.properties?.services_available;
        if (parcelServices) {
          parcelServices.split(',').forEach(service => {
            services.add(service.trim());
          });
        }
      });
      setAvailableServices(Array.from(services).filter(service => service));
    }
  }, [parcelsData]);

  // Handle sales status filter change
  const handleStatusChange = (status) => {
    const newStatus = filters.salesStatus.includes(status)
      ? filters.salesStatus.filter(s => s !== status)
      : [...filters.salesStatus, status];
    
    const newFilters = { ...filters, salesStatus: newStatus };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Handle price range change (receives full {min, max} object from chips or custom inputs)
  const handlePriceRangeChange = (newPriceRange) => {
    const isPriceActive =
      newPriceRange.min !== priceStats.min || newPriceRange.max !== priceStats.max;
    setIsPriceFilterActive(isPriceActive);

    const newFilters = {
      ...filters,
      priceRange: newPriceRange,
      isPriceFilterActive: isPriceActive
    };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Handle services filter change
  const handleServiceChange = (service) => {
    const newServices = filters.services.includes(service)
      ? filters.services.filter(s => s !== service)
      : [...filters.services, service];
    
    const newFilters = { ...filters, services: newServices };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setIsPriceFilterActive(false);
    const clearedFilters = {
      salesStatus: [],
      priceRange: { min: priceStats.min, max: priceStats.max },
      services: [],
      isPriceFilterActive: false
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  if (!isVisible) return null;

  return (
    <div className="map-filters-overlay">
      <div className="map-filters-container">
        <div className="filters-header">
          <h3 className="filters-title">
            <FaFilter className="filter-icon" />
            {language === 'ar' ? 'فلاتر الخريطة' : 'Map Filters'}
          </h3>
          <div className="filters-actions">
            <button
              className="clear-filters-btn"
              onClick={clearAllFilters}
            >
              {language === 'ar' ? 'مسح الكل' : 'Clear All'}
            </button>
            <button
              className="close-filters-btn"
              onClick={onClose}
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="filters-content">
          {/* Sales Status Filter */}
          <div className="filter-section">
            <h4 className="filter-section-title">
              {language === 'ar' ? 'حالة البيع' : 'Sales Status'}
            </h4>
            <div className="status-filters">
              <button
                className={`status-btn available ${filters.salesStatus.includes('Available') ? 'active' : ''}`}
                onClick={() => handleStatusChange('Available')}
              >
                <div className="status-icon">
                  <FaCheckCircle />
                </div>
                <span className="status-label">
                  {language === 'ar' ? 'متاح' : 'Available'}
                </span>
                <span className="status-count">{statusCounts.Available}</span>
              </button>
              
              <button
                className={`status-btn not-available ${filters.salesStatus.includes('Not Available') ? 'active' : ''}`}
                onClick={() => handleStatusChange('Not Available')}
              >
                <div className="status-icon">
                  <FaTimesCircle />
                </div>
                <span className="status-label">
                  {language === 'ar' ? 'غير متوفر' : 'Not Available'}
                </span>
                <span className="status-count">{statusCounts['Not Available']}</span>
              </button>
              
              <button
                className={`status-btn reserved ${filters.salesStatus.includes('Reserved') ? 'active' : ''}`}
                onClick={() => handleStatusChange('Reserved')}
              >
                <div className="status-icon">
                  <FaClock />
                </div>
                <span className="status-label">
                  {language === 'ar' ? 'محجوز' : 'Reserved'}
                </span>
                <span className="status-count">{statusCounts.Reserved}</span>
              </button>
              
              <button
                className={`status-btn sold ${filters.salesStatus.includes('Sold') ? 'active' : ''}`}
                onClick={() => handleStatusChange('Sold')}
              >
                <div className="status-icon">
                  <FaTimesCircle />
                </div>
                <span className="status-label">
                  {language === 'ar' ? 'مباع' : 'Sold'}
                </span>
                <span className="status-count">{statusCounts.Sold}</span>
              </button>
            </div>
          </div>

          {/* Price Range Filter */}
          <div className="filter-section">
            <h4 className="filter-section-title">
              {language === 'ar' ? 'نطاق السعر' : 'Price Range'}
            </h4>
            <PriceChipsFilter
              language={language}
              priceStats={priceStats}
              priceRange={filters.priceRange}
              onChange={handlePriceRangeChange}
            />
          </div>

          {/* Services Filter */}
          {availableServices.length > 0 && (
            <div className="filter-section">
              <h4 className="filter-section-title">
                {language === 'ar' ? 'الخدمات' : 'Services'}
              </h4>
              <div className="services-filter">
                {availableServices.map(service => (
                  <button
                    key={service}
                    className={`service-btn ${filters.services.includes(service) ? 'active' : ''}`}
                    onClick={() => handleServiceChange(service)}
                  >
                    {service}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapFilters;
